import {
  BlockStatus,
  isDraftExpired,
  liveOnly,
  projectChanges,
  sameJsonValue,
  withRevision,
  type Category,
  type LiveProject,
  type ProjectRecord,
  RecordType,
} from '@wiztime/core';
import {
  ProjectHasChildrenError,
  ProjectHierarchyError,
  ProjectHierarchyReason,
  ProjectInUseError,
} from '../errors';
import type { WriteSession } from './session';

export interface ProjectInput {
  /** Omit to create a new project. */
  readonly id?: string;
  readonly name: string;
  /** A stored colour name (bounded string, data dictionary §3); the settings page offers the palette names. */
  readonly color: string;
  readonly category: Category;
  readonly active?: boolean;
  readonly sort?: number;
  /**
   * Quick-mark block length in minutes (data dictionary §3, project.quickDurationMin). Omit to
   * keep the stored value, null to clear it. The stored number is not range-checked here:
   * readers normalise it (`effectiveQuickDurationMin`).
   */
  readonly quickDurationMin?: number | null;
  /**
   * Parent project (data dictionary §3, project hierarchy). Omit to keep the stored parent, null
   * to move the project out of its parent. A project with a parent takes the parent's category
   * (the `category` input is ignored). Refused with `ProjectHierarchyError`: a missing or
   * deleted parent, itself, a parent that is a child, a project that has children.
   */
  readonly parentId?: string | null;
  /**
   * Child project only: true = colour follows the parent. Omit: true when the project is newly
   * moved under a parent, otherwise kept as long as `color` still equals the parent's colour,
   * and false once a different colour is chosen.
   */
  readonly inheritColor?: boolean;
}

function rawParentId(data: { readonly parentId?: string | null }): string | null {
  return typeof data.parentId === 'string' && data.parentId !== '' ? data.parentId : null;
}

/** Live projects whose stored `parentId` is `parentId`. */
async function storedChildren(session: WriteSession, parentId: string): Promise<LiveProject[]> {
  return liveOnly(await session.tx.listByType(RecordType.Project)).filter(
    (project) => rawParentId(project.data) === parentId,
  );
}

/** The stored parent when it is still a live top-level project, otherwise null. */
async function keptParent(session: WriteSession, parentId: string): Promise<LiveProject | null> {
  const parent = await session.find(parentId);
  if (parent === null || parent.type !== RecordType.Project || parent.deleted || parent.data === null)
    return null;
  return rawParentId(parent.data) === null ? (parent as LiveProject) : null;
}

async function requireValidParent(
  session: WriteSession,
  projectId: string | null,
  parentId: string,
): Promise<LiveProject> {
  if (projectId !== null && parentId === projectId) {
    throw new ProjectHierarchyError(projectId, parentId, ProjectHierarchyReason.SelfParent);
  }
  const parent = await session.find(parentId);
  if (parent === null || parent.type !== RecordType.Project || parent.deleted || parent.data === null) {
    throw new ProjectHierarchyError(projectId, parentId, ProjectHierarchyReason.ParentMissing);
  }
  const grandparent = rawParentId(parent.data);
  if (grandparent !== null && (await session.find(grandparent))?.deleted === false) {
    throw new ProjectHierarchyError(projectId, parentId, ProjectHierarchyReason.ParentIsChild);
  }
  if (projectId !== null && (await storedChildren(session, projectId)).length > 0) {
    throw new ProjectHierarchyError(projectId, parentId, ProjectHierarchyReason.HasChildren);
  }
  return parent as LiveProject;
}

/**
 * Bring the stored `category` (and, for children that follow the parent, `color`) of every child
 * of `parent` in line with it, as part of the current undo step. Children already in line are
 * not written.
 */
async function syncChildren(session: WriteSession, parent: LiveProject): Promise<void> {
  const at = new Date(session.nowMs).toISOString();
  for (const child of await storedChildren(session, parent.id)) {
    const next = {
      ...child.data,
      category: parent.data.category,
      ...(child.data.inheritColor === true ? { color: parent.data.color } : {}),
    };
    if (sameJsonValue(next, child.data)) continue;
    await session.write(
      RecordType.Project,
      child.id,
      withRevision(child.data, next, projectChanges(child.data, next), at),
    );
  }
}

async function nextSort(session: WriteSession): Promise<number> {
  const projects = await session.tx.listByType(RecordType.Project);
  return projects.reduce((max, project) => Math.max(max, (project.data?.sort ?? -1) + 1), 0);
}

/** Create a project, or update the live project with the same id (a new project is active). */
export async function upsertProject(session: WriteSession, input: ProjectInput): Promise<ProjectRecord> {
  const existing = input.id === undefined ? null : await session.find(input.id);
  const current =
    existing !== null && existing.type === RecordType.Project && existing.data !== null
      ? existing.data
      : null;
  const storedParentId = current === null ? null : rawParentId(current);
  const parentId = input.parentId === undefined ? storedParentId : input.parentId;
  // A new parent is validated and refused when unusable. A kept stored parent is not: damaged
  // data (deleted parent, three levels) is normalised on read, so such a project reads as top-level.
  const parent =
    parentId === null
      ? null
      : parentId !== storedParentId
        ? await requireValidParent(session, input.id ?? null, parentId)
        : await keptParent(session, parentId);
  const isChild = parent !== null;
  const newlyAttached = isChild && parentId !== storedParentId;
  const inheritColor = isChild
    ? (input.inheritColor ??
      (newlyAttached || (current?.inheritColor === true && input.color === parent.data.color)))
    : null;
  // Spread the existing data first: fields this build does not know (written by a newer client)
  // must survive a rename (2026-10c C0, release stage one).
  const next = {
    ...current,
    name: input.name.trim(),
    color: inheritColor === true && parent !== null ? parent.data.color : input.color,
    category: parent === null ? input.category : parent.data.category,
    active: input.active ?? current?.active ?? true,
    sort: input.sort ?? current?.sort ?? (await nextSort(session)),
    ...(input.quickDurationMin === undefined ? {} : { quickDurationMin: input.quickDurationMin }),
    ...(input.parentId === undefined ? {} : { parentId: input.parentId }),
    ...(isChild ? { inheritColor } : current?.inheritColor === undefined ? {} : { inheritColor: false }),
  };
  const id = input.id ?? session.newId();
  const unchanged = current !== null && sameJsonValue(next, current);
  // A save that changes nothing writes nothing: no undo step, the last-updated time stays.
  const written = unchanged
    ? (existing as ProjectRecord)
    : await session.write(
        RecordType.Project,
        id,
        withRevision(
          current,
          next,
          current === null ? [] : projectChanges(current, next),
          new Date(session.nowMs).toISOString(),
        ),
      );
  if (!isChild && written.data !== null) await syncChildren(session, written as LiveProject);
  return written;
}

/** Deactivate or reactivate a project; its blocks and statistics are unaffected. */
export async function setProjectActive(
  session: WriteSession,
  id: string,
  active: boolean,
): Promise<ProjectRecord> {
  const project = await session.requireProject(id);
  if (project.data.active === active) return project;
  const next = { ...project.data, active };
  const at = new Date(session.nowMs).toISOString();
  return session.write(
    RecordType.Project,
    id,
    withRevision(project.data, next, projectChanges(project.data, next), at),
  );
}

/**
 * Give the live projects the display order of `ids` (settings → projects, drag sorting): each
 * project's `sort` becomes its index. Projects missing from `ids` keep their relative order after
 * the listed ones; unknown ids are ignored. Only projects whose `sort` changes are written, and
 * `sort` is not a tracked field, so no revision entry is added.
 */
export async function reorderProjects(
  session: WriteSession,
  ids: readonly string[],
): Promise<ProjectRecord[]> {
  const live = liveOnly(await session.tx.listByType(RecordType.Project)).sort(
    (a, b) => a.data.sort - b.data.sort || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  const byId = new Map(live.map((project) => [project.id, project]));
  const listed = [...new Set(ids)].filter((id) => byId.has(id));
  const order = [...listed, ...live.map((project) => project.id).filter((id) => !listed.includes(id))];
  const written: ProjectRecord[] = [];
  for (const [index, id] of order.entries()) {
    const project = byId.get(id);
    if (project === undefined || project.data.sort === index) continue;
    written.push(await session.write(RecordType.Project, id, { ...project.data, sort: index }));
  }
  return written;
}

/**
 * Delete a project that no visible block uses (ProjectInUseError otherwise). Expired drafts
 * of the project are purged, and templates of the project are deleted with it.
 */
export async function deleteProject(session: WriteSession, id: string): Promise<void> {
  await session.requireProject(id);
  const children = await storedChildren(session, id);
  if (children.length > 0) throw new ProjectHasChildrenError(id, children.length);
  const settings = await session.settings();
  const blocks = (await session.tx.listByType(RecordType.TimeBlock)).filter(
    (block) => block.data?.projectId === id,
  );
  const expired = blocks.filter(
    (block) =>
      block.data !== null &&
      block.data.status === BlockStatus.Draft &&
      isDraftExpired(block.data.personalDate, settings, session.nowMs),
  );
  const inUse = blocks.length - expired.length;
  if (inUse > 0) throw new ProjectInUseError(id, inUse);
  for (const draft of expired) await session.purge(draft);
  for (const template of await session.tx.listByType(RecordType.Template)) {
    if (template.data?.projectId === id) await session.write(RecordType.Template, template.id, null);
  }
  await session.write(RecordType.Project, id, null);
}
